import { Api } from 'lib';
import { Link } from 'react-router-dom';
import { CLAIM_LABELS, POLL_STATES, QUIZ_STATES, timeOf, when, winningClaim } from '../labState';
import { Btn, Empty, FeatureCard, ItemRow, Pill, TeamColumn } from '../ui';
import FanWaveCard from './FanWaveCard';

const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const stamp = () => new Date().toTimeString().slice(0, 5);
const small = 'h-7 text-[12px]';

const sidesOf = (game) => [
  { side: 'home', teamId: game.home, teamName: game.homeName, opponent: game.awayName },
  { side: 'away', teamId: game.away, teamName: game.awayName, opponent: game.homeName }
];

const ManageLink = ({ to, children }) => (
  <Link className="ml-auto text-[12px] text-[#4F46E5] hover:underline" to={to}>
    {children}
  </Link>
);

const TeamChannelCard = ({ game, state }) => (
  <FeatureCard
    title="Team Channel"
    summary="Poll and Quick Quiz share one slot on each team's channel. The rules below are checked in order and the first that matches decides what fans see. This is the signed-out fan's view."
  >
    {sidesOf(game).map(({ side, teamName }) => {
      const surface = state[side].surface;
      const claim = winningClaim(surface);
      const isQuiz = surface.surface === 'quick_quiz';
      const item = isQuiz
        ? state[side].quizzes.find((q) => String(q._id) === String(surface.quizId))
        : state[side].polls.find((p) => String(p._id) === String(surface.pollId));
      return (
        <TeamColumn
          key={side}
          side={side}
          teamName={teamName}
          status={{
            tone: claim ? 'live' : 'idle',
            label: claim
              ? `Showing the ${isQuiz ? 'Quick Quiz' : claim === 'poll_open' ? 'Poll' : 'Poll results'}`
              : 'Showing nothing',
            detail: item?.question || (claim ? null : 'No rule matched, so the slot is empty')
          }}
        >
          <ol className="flex flex-col gap-1">
            {(surface.claimOrder || []).map((rule, i) => {
              const won = rule === claim;
              return (
                <li
                  key={rule}
                  className={`flex items-center gap-2 rounded-md px-2 py-1 text-[12px] ${
                    won ? 'bg-[#EEF2FF] text-[#3730A3] font-semibold' : 'text-[#94A3B8]'
                  }`}
                >
                  <span className="w-4 text-right tabular-nums">{i + 1}.</span>
                  <span>{CLAIM_LABELS[rule] || rule}</span>
                  {won && <span className="ml-auto">← matched</span>}
                </li>
              );
            })}
          </ol>
        </TeamColumn>
      );
    })}
  </FeatureCard>
);

const PollsCard = ({ game, state, act, busy }) => (
  <FeatureCard
    title="Polls"
    summary="A game-day poll is open before kickoff, closes at kickoff and then shows its results. At half-time the quiz takes the Team Channel; the poll stays closed underneath."
  >
    {sidesOf(game).map(({ side, teamId, teamName }) => {
      const { polls, surface } = state[side];
      const lead =
        polls.find((p) => String(p._id) === String(surface.pollId)) ||
        polls.find((p) => p.isActive) ||
        polls[0];
      const meaning = lead && (POLL_STATES[lead.state] || { tone: 'idle', label: lead.state });
      const status = lead
        ? {
            tone: meaning.tone,
            label: meaning.detail ? `${meaning.label} — ${meaning.detail}` : meaning.label,
            detail: lead.question
          }
        : {
            tone: 'idle',
            label: 'No poll on this game',
            detail: 'Create one to test the pre-match poll'
          };
      const create = () =>
        act('Create game-day poll', () =>
          Api.post(
            '/admin/polls/create',
            {
              question: `Lab poll ${stamp()}: who wins?`,
              teamId,
              gameId: game._id,
              pollType: 'game_day',
              options: [{ text: game.homeName }, { text: game.awayName }, { text: 'Draw' }]
            },
            null,
            { silent: true }
          )
        );
      return (
        <TeamColumn
          key={side}
          side={side}
          teamName={teamName}
          status={status}
          actions={
            <>
              <Btn className={small} disabled={busy} onClick={create}>
                + Create game-day poll
              </Btn>
              <ManageLink to={`/admin/polls?team=${teamId}`}>Manage polls →</ManageLink>
            </>
          }
        >
          {polls.length > 1 &&
            polls.map((p) => (
              <ItemRow
                key={p._id}
                title={p.question}
                state={POLL_STATES[p.state]}
                onChannel={String(p._id) === String(surface.pollId)}
                meta={<span>{fmt(p.totalSubmissions)} votes</span>}
              />
            ))}
          {polls.length === 1 && (
            <p className="text-[12px] text-[#64748B]">
              {fmt(lead.totalSubmissions)} votes
              {String(lead._id) === String(surface.pollId) ? ' · on the Team Channel now' : ''}
            </p>
          )}
        </TeamColumn>
      );
    })}
  </FeatureCard>
);

const QuizCard = ({ game, state, act, busy }) => (
  <FeatureCard
    title="Quick Quiz"
    summary="A half-time quiz opens at the half-time whistle and takes answers through the break. Afterwards it stays on display with its answers for a while, then the poll results come back."
  >
    {sidesOf(game).map(({ side, teamId, teamName, opponent }) => {
      const { quizzes, surface } = state[side];
      const lead =
        quizzes.find((q) => String(q._id) === String(surface.quizId)) ||
        quizzes.find((q) => q.isActive) ||
        quizzes[0];
      const meaning = lead && (lead.isActive ? QUIZ_STATES[lead.state] : null);
      const timing =
        lead?.state === 'ready_to_play'
          ? lead.closesAt && `Closes ${when(lead.closesAt)}`
          : lead?.state === 'coming_up'
            ? lead.opensAt
              ? `Opens ${when(lead.opensAt)}`
              : 'Opens at the half-time whistle'
            : lead?.closesAt && `Closed ${when(lead.closesAt)}`;
      const status = !lead
        ? { tone: 'idle', label: 'No quiz on this game', detail: 'Create one to test half-time' }
        : !meaning
          ? { tone: 'idle', label: 'Inactive', detail: 'Switched off in Quick Quiz' }
          : { tone: meaning.tone, label: `${meaning.label} — ${meaning.detail}`, detail: timing };
      const create = () =>
        act('Create half-time quiz', () =>
          Api.post(
            '/admin/quizzes/create',
            {
              question: `Lab half-time quiz ${stamp()}: who leads at the break?`,
              teamId,
              gameId: game._id,
              quizType: 'half_time',
              rewardPoints: 50,
              isActive: true,
              answers: [
                { text: teamName, isCorrect: true },
                { text: opponent, isCorrect: false }
              ]
            },
            null,
            { silent: true }
          )
        );
      return (
        <TeamColumn
          key={side}
          side={side}
          teamName={teamName}
          status={status}
          actions={
            <>
              <Btn className={small} disabled={busy} onClick={create}>
                + Create half-time quiz
              </Btn>
              <ManageLink to={`/admin/quick-quiz?team=${teamId}`}>Manage quizzes →</ManageLink>
            </>
          }
        >
          {quizzes.length > 1 &&
            quizzes.map((q) => (
              <ItemRow
                key={q._id}
                title={q.question}
                state={q.isActive ? QUIZ_STATES[q.state] : { tone: 'idle', label: 'Inactive' }}
                onChannel={String(q._id) === String(surface.quizId)}
                meta={
                  <>
                    <span>{fmt(q.answers)} answers</span>
                    {q.opensAt && <span>Opens {timeOf(q.opensAt)}</span>}
                    {q.closesAt && <span>Closes {timeOf(q.closesAt)}</span>}
                  </>
                }
              />
            ))}
          {quizzes.length === 1 && (
            <p className="text-[12px] text-[#64748B]">
              {lead.question} · {fmt(lead.answers)} answers
              {String(lead._id) === String(surface.quizId) ? ' · on the Team Channel now' : ''}
            </p>
          )}
        </TeamColumn>
      );
    })}
  </FeatureCard>
);

const Line = ({ label, children }) => (
  <div className="flex items-center justify-between gap-3 text-[12px]">
    <span className="text-[#64748B]">{label}</span>
    <span className="text-right text-[#0F172A]">{children}</span>
  </div>
);

const PointsCard = ({ game, state }) => (
  <FeatureCard
    title="Points & rankings"
    summary="Player of the Match voting, score predictions (points are given at half-time and full time) and the GloryBoard ranking window, which opens at half-time."
  >
    {sidesOf(game).map(({ side, teamName }) => {
      const { potm, predictions, gloryBoard } = state[side];
      return (
        <TeamColumn key={side} side={side} teamName={teamName}>
          <Line label="Player of the Match">
            {potm ? (
              <>
                <Pill tone={potm.status === 'open' ? 'live' : 'done'}>{potm.status}</Pill>{' '}
                {fmt(potm.totalVotes)} votes
              </>
            ) : (
              'No poll'
            )}
          </Line>
          {predictions.length ? (
            predictions.map((g) => (
              <Line
                key={g._id}
                label={`${g.type === 'half_time' ? 'Half-time' : 'Full-time'} predictions`}
              >
                {fmt(g.predictions)} picks · {fmt(g.votes)} votes ·{' '}
                <span className={g.scored ? 'text-[#166534] font-semibold' : ''}>
                  {g.scored ? `${fmt(g.scored)} scored` : 'not scored yet'}
                </span>
              </Line>
            ))
          ) : (
            <Line label="Predictions">None</Line>
          )}
          <Line label="GloryBoard window">
            {gloryBoard.state === 'none' ? (
              'Opens at half-time'
            ) : (
              <>
                <Pill tone={gloryBoard.state === 'open' ? 'live' : 'done'}>{gloryBoard.state}</Pill>{' '}
                {gloryBoard.state === 'open' ? 'closes' : 'closed'}{' '}
                {new Date(gloryBoard.closesAt).toLocaleString([], {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                {!gloryBoard.fromThisGame && ' · from an earlier match'}
              </>
            )}
          </Line>
        </TeamColumn>
      );
    })}
  </FeatureCard>
);

/** Every game-day feature on the selected game, one card per feature, home beside away. */
const GameDayFeatures = ({ game, state, fanwave, act, busy }) => {
  if (!state?.home || !state?.away) {
    return (
      <section className="bg-white border border-[#E2E8F0] rounded-xl p-4">
        <Empty>Loading game-day features…</Empty>
      </section>
    );
  }
  return (
    <>
      <TeamChannelCard game={game} state={state} />
      <PollsCard game={game} state={state} act={act} busy={busy} />
      <QuizCard game={game} state={state} act={act} busy={busy} />
      <FanWaveCard sides={sidesOf(game)} game={game} fanwave={fanwave} act={act} busy={busy} />
      <PointsCard game={game} state={state} />
    </>
  );
};

export default GameDayFeatures;
